// Copyright (c) 2025 Audere, All Rights Reserved

import { useCallback, useEffect, useState } from "react";
import { Backdrop, Box, CircularProgress } from "@mui/material";
import { useSelector } from "react-redux";
import { GoogleLogin, GoogleOAuthProvider, type CredentialResponse } from "@react-oauth/google";
import { QueryStatus } from "@reduxjs/toolkit/query";
import { type FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useTranslation } from "react-i18next";

import { selectCurrentToken } from "../features/auth/authSlice";
import { createStyleSheet } from "../utils/styles";
import { useLazyOpenIdAuthenticateQuery } from "../features/auth/authApiSlice";
import { HttpStatus, type LoginButtonProps } from "../types/auth";
import { logEvent, useLazyGetRolesQuery } from "../features/api/apiSlice";
import { useAppDispatch } from "../features/store";

const LoginButton = (props: LoginButtonProps) => {
  const { googleClientId, onAuthSuccess } = props;
  const isLoggedIn = !!useSelector(selectCurrentToken);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [isTryingToLogin, setIsTryingToLogin] = useState(false);
  const [openIdAuthenticate, openIdAuthenticateResult] = useLazyOpenIdAuthenticateQuery();
  const {
    data: authData,
    error: rawAuthError,
    isError: isAuthError,
    status: authStatus,
  } = openIdAuthenticateResult;
  const authError = rawAuthError as FetchBaseQueryError;
  const [getRoles, getRolesResult] = useLazyGetRolesQuery();
  const {
    data: rolesData,
    error: rolesDataError,
    isError: isRolesDataError,
    status: rolesDataStatus,
  } = getRolesResult;

  const onAuthError = useCallback(
    (errorMsg: { status?: number; errorDescription?: string }) => {
      logEvent(dispatch, {
        event: "userLogin",
        data: { success: false },
      });
      throw errorMsg;
    },
    [dispatch]
  );

  const loginError = useCallback(() => {
    setIsTryingToLogin(false);
    onAuthError({ errorDescription: t("ErrorPage:authenticationError") });
  }, [onAuthError, t]);
  const loginSuccess = useCallback(
    (credentialResponse: CredentialResponse) => {
      setIsTryingToLogin(true);
      openIdAuthenticate(credentialResponse.credential!);
    },
    [openIdAuthenticate]
  );

  useEffect(() => {
    if (!isLoggedIn && authData) {
      const organizations = Object.keys(rolesData?.organizations || {});
      const hasOrganizations = organizations.length > 0;
      if (rolesDataStatus === QueryStatus.fulfilled && hasOrganizations) {
        const orgId = organizations[0];
        onAuthSuccess({
          ...authData,
          organizationId: orgId,
          roles: rolesData?.organizations[orgId]?.roles || [],
        });
      } else if (isRolesDataError || (rolesData && !hasOrganizations)) {
        onAuthError({
          status:
            rolesData && !hasOrganizations
              ? HttpStatus.UNAUTHORIZED
              : ((rolesDataError as FetchBaseQueryError)?.status as number),
          errorDescription:
            rolesData && !hasOrganizations ? t("ErrorPage:unauthorizedRoleMessage") : undefined,
        });
      }
      setIsTryingToLogin(false);
    }
  }, [
    authData,
    onAuthSuccess,
    onAuthError,
    isLoggedIn,
    rolesDataStatus,
    rolesData,
    isRolesDataError,
    rolesDataError,
    authError,
    t,
  ]);
  useEffect(() => {
    if (authStatus === QueryStatus.fulfilled) {
      getRoles(authData!.token!);
    } else if (isAuthError) {
      setIsTryingToLogin(false);
      onAuthError({
        status: authError.status as number,
      });
    }
  }, [authStatus, authData, isAuthError, authError, onAuthError, getRoles]);

  return (
    <GoogleOAuthProvider clientId={googleClientId} data-testid="google-oauth-provider">
      <Box>
        <GoogleLogin
          onSuccess={loginSuccess}
          onError={loginError}
          size="large"
          theme="outline"
          data-testid="google-login-button"
        />
      </Box>
      <Backdrop sx={styles.backdrop} open={isTryingToLogin} data-testid="backdrop">
        <CircularProgress color="inherit" />
      </Backdrop>
    </GoogleOAuthProvider>
  );
};

const styles = createStyleSheet({
  backdrop: {
    color: "white",
    zIndex: theme => theme.zIndex.drawer + 1,
  },
});

export default LoginButton;
